import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Lightbulb, Trophy } from 'lucide-react';
import { useLanguage } from './Localization'; // Import the custom hook

const FeatureCard = ({ icon: Icon, title, description, gradient, glow }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 40 },
      show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
    }}
    whileHover={{ y: -8 }}
    className="group text-center px-6 py-10 bg-white rounded-3xl shadow-card hover:shadow-xl transition-shadow duration-300"
  >
    <div
      className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mx-auto mb-7 shadow-lg ${glow} group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}
    >
      <Icon className="w-10 h-10 text-white" />
    </div>
    <h3 className="font-display text-2xl font-semibold text-gray-800 mb-4">{title}</h3>
    <p className="text-gray-500 leading-relaxed max-w-sm mx-auto text-lg">{description}</p>
  </motion.div>
);

const Features = () => {
  const { t } = useLanguage(); // Use language context

  const features = [
    {
      icon: Globe,
      title: t('onlineModeTitle'),
      description: t('onlineModeDescription'),
      gradient: 'from-sky-400 to-blue-600',
      glow: 'shadow-sky-500/30',
    },
    {
      icon: Lightbulb,
      title: t('aiTitle'),
      description: t('aiDescription'),
      gradient: 'from-brand-400 to-emerald-600',
      glow: 'shadow-emerald-500/30',
    },
    {
      icon: Trophy,
      title: t('rankingsTitle'),
      description: t('rankingsDescription'),
      gradient: 'from-fuchsia-400 to-purple-600',
      glow: 'shadow-purple-500/30',
    },
  ];

  return (
    <section id="features" className="pt-12 pb-24 px-4 scroll-mt-24">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl sm:text-5xl font-bold bg-gradient-to-r from-brand-600 to-emerald-500 bg-clip-text text-transparent mb-6">
            {t('featuresTitle')}
          </h2>
          <p className="text-gray-500 max-w-3xl mx-auto text-xl">
            {t('featuresDescription')}
          </p>
        </motion.div>

        <motion.div
          variants={{ show: { transition: { staggerChildren: 0.18 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
